// calendar-kakao-maps-shared (2026-09-02) — TravelMap.tsx 에 있던 SDK 로더를 공용으로 뺐다.
// LocationPicker(장소 검색)도 같은 SDK 인스턴스를 쓰는데, 검색은 services 라이브러리가 필요해서
// (kakao.maps.services.Places) 이 공용 로더가 항상 &libraries=services 를 붙여서 부른다 —
// TravelMap 은 안 쓰지만 같이 실려도 무해하고, 스크립트 태그를 두 번(다른 옵션으로) 넣으면
// 카카오 SDK 가 충돌하므로 반드시 로더를 하나로 통일해야 한다.

// ⛔ 이 키는 **apps/frontend/.env.*** 에 있어야 한다. 레포 루트 `.env.local` 은 vite 의
// envDir(= vite root = apps/frontend) «밖»이라 **안 읽힌다** — 2026-09-29 에 실제로 겪었다:
// 사용자가 루트에 키를 넣었는데 화면은 그대로 «검색이 안 되는» 상태였고, 로더가
// 'no-kakao-js-key' 로 거절하는 것을 «오류 이름으로» 가르고서야 자리를 찾았다.
//   ⚠ 그리고 그 실패는 «조용하다» — 아래 호출부가 catch 로 삼켜 자유 텍스트로 떨어진다.
//     그래서 「안 뜬다」만으로는 키 문제인지 도메인 문제인지 못 가른다. 이름으로 갈라라:
//       no-kakao-js-key        env 가 vite 에 안 실렸다(자리·재시작)
//       kakao-sdk-load-timeout 도메인 미등록일 가능성이 가장 크다
//       kakao-sdk-load-failed  스크립트 자체를 못 받았다

declare global {
  interface Window {
    kakao: any;
  }
}

let kakaoLoadPromise: Promise<void> | null = null;

export function loadKakaoMaps(): Promise<void> {
  if (window.kakao?.maps) return Promise.resolve();
  if (kakaoLoadPromise) return kakaoLoadPromise;
  const appkey = import.meta.env.VITE_KAKAO_JS_KEY as string | undefined;
  if (!appkey) return Promise.reject(new Error('no-kakao-js-key'));
  // 2026-09-02 — 처음 도메인 미등록 상태로 한 번 실패하면(카카오 콘솔에 나중에 도메인을
  // 추가해도) 이 모듈 스코프 promise 가 "실패"로 굳어 있어서, 새로고침 전까진 재시도조차
  // 안 하고 계속 같은 실패를 돌려주던 버그. 실패 시 캐시를 비워 다음 호출이 다시 시도하게
  // 한다. 또한 도메인 미등록일 때 카카오 SDK가 script.onerror 는 안 던지고(스크립트 자체는
  // 200으로 로드됨) kakao.maps.load 콜백만 영영 안 부르는 경우가 있어 타임아웃도 추가한다.
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=${appkey}&autoload=false&libraries=services`;
    script.async = true;
    const timer = window.setTimeout(() => reject(new Error('kakao-sdk-load-timeout')), 8000);
    script.onerror = () => { window.clearTimeout(timer); reject(new Error('kakao-sdk-load-failed')); };
    script.onload = () => {
      try {
        window.kakao.maps.load(() => { window.clearTimeout(timer); resolve(); });
      } catch (e) {
        window.clearTimeout(timer);
        reject(e instanceof Error ? e : new Error('kakao-sdk-init-failed'));
      }
    };
    document.head.appendChild(script);
  }).catch((err) => {
    kakaoLoadPromise = null;
    throw err;
  });
  kakaoLoadPromise = promise;
  return promise;
}
